import React, { CSSProperties, ElementType, JSXElementConstructor, ReactNode, Ref, forwardRef, useMemo } from 'react';

import { addRootNodeClassName, cn } from '../../lib/css/className/className';
import { Color } from '../../internal/types/designSystemTypes';
import './TextGeneric.css';
import { useInternalVerticalAlign } from '../../hooks/useVerticalAlign/useVerticalAlign';
import { TypographyRole, useTypography } from '../../hooks/useTypography/useTypography';
import { HStack, Stack, StackGap, VStack } from '../Stack';
import { HelpProps } from '../Help/types';
import { VerticalAlignAddonSize } from '../../hooks/useVerticalAlign/types';
import { TestProps } from '../../internal/types/testTypes'; // не нужно сокращать, будет циклическая зависимость
import { StackAlignBase } from '../Stack/types';

import type { TextPreset } from './types';

/* Оставила класс Текст, не нужно убирать */
const cls = cn('Text');

export type TextAlign = 'left' | 'center' | 'right';

export const textAlignToStack: Record<TextAlign, StackAlignBase> = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
};

export type TextTruncationProps = {
    /**
     * Признак обрезания текста при переполнении контейнера.
     * После обрезки контент занимает одну строку.
     * Для многострочного текста можно использовать свойство `maxLines`
     */
    isTruncated?: boolean;

    /**
     * Максимальное количество строк, доступных к прочтению без обрезания контента
     */
    maxLines?: number;
};

interface AddonOptions {
    /**
     * Размер аддона (иконки или другого дополнительного элемента), расположенного близко к тексту.
     * Перечисленные размеры определены дизайн-системой
     */
    addonSize: VerticalAlignAddonSize;

    /**
     * Значение отступов между основным текстом и аддоном (иконкой).
     */
    gap: StackGap;
}

export const textWithAddonStyles: Record<TextPreset, AddonOptions> = {
    '10-12': { addonSize: '12', gap: '4' },
    '10-12-M': { addonSize: '12', gap: '4' },
    '12-16': { addonSize: '12', gap: '4' },
    '12-16-M': { addonSize: '12', gap: '4' },
    '14-20': { addonSize: '16', gap: '4' },
    '14-20-M': { addonSize: '16', gap: '6' },
    '16-20': { addonSize: '16', gap: '8' },
    '16-20-M': { addonSize: '16', gap: '8' },
    '16-24': { addonSize: '16', gap: '8' },
    '16-24-M': { addonSize: '16', gap: '8' },
    '20-28': { addonSize: '16', gap: '8' },
    '20-28-M': { addonSize: '16', gap: '8' },
    '24-32': { addonSize: '16', gap: '8' },
    '24-32-M': { addonSize: '16', gap: '8' },
    '32-40': { addonSize: '24', gap: '8' },
    '32-40-M': { addonSize: '24', gap: '8' },
    '12-16 + 12-16': { addonSize: '12', gap: '4' },
    '12-16-M + 12-16': { addonSize: '12', gap: '4' },
    '14-20 + 12-16': { addonSize: '16', gap: '6' },
    '14-20-M + 12-16': { addonSize: '16', gap: '6' },
    '14-20 + 14-20': { addonSize: '16', gap: '6' },
    '14-20-M + 14-20': { addonSize: '16', gap: '6' },
    '16-20 + 12-16': { addonSize: '16', gap: '8' },
    '16-20-M + 12-16': { addonSize: '16', gap: '8' },
    '16-24 + 12-16': { addonSize: '16', gap: '8' },
    '16-24 + 16-24': { addonSize: '16', gap: '8' },
    '16-24-M + 12-16': { addonSize: '16', gap: '8' },
    '16-24-M + 16-24': { addonSize: '16', gap: '8' },
    '20-28-M + 16-24': { addonSize: '16', gap: '8' },
    '20-28 + 20-28': { addonSize: '16', gap: '8' },
    '20-28-M + 20-28': { addonSize: '16', gap: '8' },
    '24-32-M + 16-24': { addonSize: '16', gap: '8' },
    '32-40-M + 20-28': { addonSize: '24', gap: '8' },
};

export interface TextProps extends TestProps, TextTruncationProps {
    /**
     * Названия классов для элемента
     */
    className?: string;

    /**
     * Цветовая палитра для текста и описания.
     * Перечисленные цвета определены дизайн-системой.
     * При `current` используется значение `text-main`/`text-supplementary` из текущего цветового скина
     * @default gray
     */
    color?: Color | 'current';

    /**
     * Размер шрифта и высота строки для текста.
     * Значения с парами предназначены для задания стиля основному тексту и описанию.
     * Перечисленные сочетания размеров определены дизайн-системой
     * @default 14-20
     */
    textSize?: TextPreset;

    /**
     * Основной текст (заголовок или абзац)
     */
    children?: ReactNode;

    /**
     * Дополнительный поясняющий текст (описание к заголовку)
     */
    caption?: ReactNode;

    /**
     * Выравнивание текстового блока по горизонтали.
     * @default 'left'
     */
    textAlign?: TextAlign;

    /**
     * Компонент со всплывающей подсказкой при клике на знак вопроса.
     * При передаче данного параметра рядом с текстом рисуется иконка `Help` соответствующего размера.
     * @param helpProps - параметры для передадчи во всплывающую подсказку (управляют размером, цветом и выравниванием)
     */
    help?: (helpProps: HelpProps) => ReactNode;

    /**
     * Аддон слева от текста (иконка или другой дополнительный элемент).
     * Размер аддона задаётся в соответствии с размером текста и определён дизайн-системой.
     */
    addonLeft?: ReactNode;

    /**
     * Аддон справа от текста (иконка или другой дополнительный элемент).
     * Размер аддона задаётся в соответствии с размером текста и определён дизайн-системой.
     */
    addonRight?: ReactNode;

    /**
     * Роль элемента типографики: основной (main) или дополнительный (supplementary) текст.
     * От роли зависят возвращаемые textClasses для одиночного текстового элемента
     * @default 'main'
     */
    role?: TypographyRole;

    /**
     * Альтернативный тег для компонента (например, 'h1')
     * @default span
     */
    as?: ElementType | JSXElementConstructor<unknown>;

    /**
     * Жирность текста
     */
    bold?: boolean;

    /**
     * Title — текст подсказки при наведении мышки,
     * полезен в случае, если текст виден не полностью.
     * в случае, если значение не определено, текст показывается не полностью и `children` имеет тип string,
     * то будет использовано значение `children`.
     */
    title?: string;

    /**
     * Реф для контейнера текста
     */
    ref?: Ref<HTMLElement>;
}

export interface ElementTagProps {
    /**
     * Атрибут data-testid для тестирования
     */
    'data-testid'?: string;

    /**
     * Названия классов для элемента
     */
    className?: string;

    /**
     * CSS-стиль, применяемый к элементу
     */
    style?: CSSProperties;

    /**
     * Title — текст подсказки при наведении мышки,
     * полезен в случае, если текст виден не полностью.
     * в случае, если значение не определено, текст показывается не полностью и `children` имеет тип string,
     * то будет использовано значение `children`.
     */
    title?: string;

    /**
     * Основной текст (заголовок или абзац)
     */
    children: ReactNode;
}

/**
 * @internal Внутренний компонент для создания текстовых раскладок
 */
export const TextGeneric = forwardRef<HTMLElement, TextProps>(function TextGeneric(
    {
        className,
        color = 'gray',
        textSize = '14-20',
        children,
        caption,
        textAlign = 'left',
        help,
        addonLeft,
        addonRight,
        role = 'main',
        isTruncated,
        maxLines,
        bold,
        title,
        'data-testid': dataTestid = 'Text',
        as: ElementTag = 'span',
    },
    ref,
) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- полиморфный тег принимает общие DOM-пропсы
    const ContentElement = ElementTag as React.ComponentType<Record<string, unknown>>;
    const { addonSize, gap } = textWithAddonStyles[textSize];

    const {
        textClasses: textAlignClasses,
        captionClasses: captionAlignClasses,
        addonClasses: addonAlignClasses,
    } = useInternalVerticalAlign({
        textSize,
        addonSize,
    });

    const { textClasses: textTypographyClasses, captionClasses: captionTypographyClasses } = useTypography({
        size: textSize,
        color,
        role,
        bold,
    });

    const hasAddons = Boolean(addonLeft || addonRight);
    const hasCaption = Boolean(caption);

    const lineClamp = Number(maxLines) > 0 ? maxLines : undefined;
    const style = useMemo<CSSProperties>(() => ({ WebkitLineClamp: lineClamp }), [lineClamp]);

    const textTitle = useMemo(() => {
        if (title) return title;

        return (isTruncated || Boolean(lineClamp)) && typeof children === 'string' ? children : undefined;
    }, [title, isTruncated, lineClamp, children]);

    const iconHelp = help?.({ iconContainerClassName: cls('help', [addonAlignClasses]), size: addonSize });
    const hasHelp = help && iconHelp;
    let content = (
        <ContentElement
            data-testid={`${dataTestid}.Content`}
            className={cls('text', { truncated: isTruncated, lineClamped: Boolean(lineClamp) }, [
                textAlignClasses,
                textTypographyClasses,
            ])}
            style={style}
            title={textTitle}
            ref={ref}
        >
            {children}

            {/* создание всплывающей подсказки при использовании Help, когда текст не обрезается */}
            {iconHelp && !isTruncated && !lineClamp && <span className={cls('helpWrapper')}>{iconHelp}</span>}
        </ContentElement>
    );

    if (hasAddons || hasHelp) {
        content = (
            <HStack
                alignItems={textAlignToStack[textAlign]}
                gap={gap}
                className={cls('addonContainer', { truncated: isTruncated })}
            >
                {addonLeft && <Stack className={cls('addonLeft', [addonAlignClasses])}>{addonLeft}</Stack>}
                {content}

                {/* создание всплывающей подсказки при использовании Help, когда текст обрезается */}
                {(isTruncated || Boolean(lineClamp)) && iconHelp}
                {addonRight && <Stack className={cls('addonRight', [addonAlignClasses])}>{addonRight}</Stack>}
            </HStack>
        );
    }

    if (hasCaption) {
        const captionTitle = (isTruncated || Boolean(lineClamp)) && typeof caption === 'string' ? caption : undefined;

        content = (
            <VStack alignItems={textAlignToStack[textAlign]}>
                {content}

                <span
                    className={cls('caption', { truncated: isTruncated, lineClamped: Boolean(lineClamp) }, [
                        captionAlignClasses,
                        captionTypographyClasses,
                    ])}
                    style={style}
                    title={captionTitle}
                    data-testid={`${dataTestid}.Caption`}
                >
                    {caption}
                </span>
            </VStack>
        );
    }

    if (textAlign !== 'left' && !hasCaption) {
        content = <VStack alignItems={textAlignToStack[textAlign]}>{content}</VStack>;
    }

    return addRootNodeClassName(
        content,
        cls({ textAlign, truncated: isTruncated, lineClamped: Boolean(lineClamp) }, [className]),
        { 'data-testid': dataTestid },
    );
});
